import React from "react";
import colorsStore from "shared/store/colorsStore";
import {
  formatItalianNumber,
  formatItalianPercentage,
  formatItalianPercentageNo100,
  formatItalianNumberOrDefault,
} from "utils/formatUtils";
import { riskProfileMapEngToIta } from "utils/misc";
import styles from "./caratteristicheDiPortafoglio.module.scss";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "shared/components/ui/tooltip";
import { QuestionMarkIcon } from "shared/assets/svg/QuestionMarkIcon";

type SectionChildrenProps = {
  data: any;
};
const CaratteristicaDiPortafoglio: React.FC<SectionChildrenProps> = ({
  data,
}) => {
  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  if (!data) {
    return <></>;
  }
  // Insurance guided portfolios: no dividend yield, no Final Score Advice. On the
  // 3-column layout every other parameter keeps its full-diagnosis column and
  // each column closes up (dense placement).
  const isInsurance = data?.is_insurance_portfolio === true;
  const column = (n: 1 | 2 | 3) =>
    isInsurance ? styles[`insuranceColumn${n}`] : undefined;

  return (
    <div className={styles.container}>
      <div
        className={
          isInsurance
            ? `${styles.gridContainer} ${styles.insuranceGrid}`
            : styles.gridContainer
        }
        style={{ color: colorsConfig.finecoBlack }}
      >
        <Caratteristica
          title={t("portfolio.rendimentoAtteso")}
          className={column(1)}
          value={": " + formatItalianPercentage(data?.return)}
        />

        {!isInsurance && (
          <Caratteristica
            title={t("portfolio.dividendYield1y")}
            value={": " + formatItalianPercentage(data?.dividend_yield)}
          />
        )}
        <Caratteristica
          title={t("portfolio.ratingEsgFineco")}
          className={column(3)}
          value={": " + formatItalianNumber(data?.esg)}
        />
        <Caratteristica
          title={t("portfolio.var")}
          className={column(1)}
          value={": " + formatItalianPercentage(data?.var)}
        />
        <Caratteristica
          title={t("portfolio.ytm")}
          className={column(2)}
          value={": " + formatItalianPercentageNo100(data?.ytm)}
        />
        <Caratteristica
          title={t("portfolio.finalScore")}
          className={column(3)}
          value={": " + formatItalianNumberOrDefault(data?.final_score, "n.d.")}
        />
        <Caratteristica
          title={t("portfolio.sharpe")}
          className={column(1)}
          value={": " + formatItalianNumber(data?.sharpe, 3)}
        />
        <Caratteristica
          title={t("portfolio.duration")}
          className={column(2)}
          value={": " + formatItalianNumber(data?.duration) + " anni"}
        />
        {!isInsurance && (
          <Caratteristica
            title={t("portfolio.finalScoreAdvice")}
            value={": " + formatItalianNumberOrDefault(data?.final_score_advice, "n.d.")}
          />
        )}
        {data?.financial_plan && (
          <Caratteristica
            title={t("portfolio.pianoFinanziario")}
            className={column(1)}
            value={": " + data?.financial_plan}
          />
        )}
        <Caratteristica
          title={t("portfolio.costiStrumenti")}
          className={column(2)}
          value={": " + formatItalianPercentage(data?.total_cost)}
          description={t("portfolio.costiStrumentiDescription")}
        />
        {data?.risk_profile && (
          <Caratteristica
            title={t("portfolio.profiloDiRischio")}
            className={column(3)}
            value={": " + riskProfileMapEngToIta(data?.risk_profile)}
          />
        )}
      </div>
    </div>
  );
};
export default React.memo(
  CaratteristicaDiPortafoglio,
  (prevProps: SectionChildrenProps, nextProps: SectionChildrenProps) => {
    return prevProps.data === nextProps.data;
  },
);

type StatProps = {
  title: string;
  className?: string;
  value: string;
  description?: string;
};

const Caratteristica: React.FC<StatProps> = ({
  title,
  className,
  value,
  description,
}) => {
  return (
    <div
      className={className ? `${styles.statsTitle} ${className}` : styles.statsTitle}
    >
      {title}

      {description && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <div className={styles.infoIcon}>
                <QuestionMarkIcon viewBox="0.5 0.5 17 17" />
              </div>
            </TooltipTrigger>
            <TooltipContent>{description}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <span className={styles.statsValue}>{value}</span>
    </div>
  );
};
